<script>
  import { onMount, tick } from "svelte";
  import { Modal } from "svelte-utilities";
  import cloneDeep from "lodash/cloneDeep";

  import CopyableTable from "#lib/datatable/CopyableTable.svelte";
  import CaretDown from "@local/assets/icons/caret-down.svg";
  import CaretUp from "@local/assets/icons/caret-up.svg";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";

  import copyToClipboard from "#src/extensions/copy-to-clipboard.js";
  import { selectedLocations } from "#src/stores/ui.js";
  import { isLocationVisible } from "#src/extensions/filter-items.js";

  import eb from "#src/extensions/event-bus.js";

  export let group;
  export let docLocations;
  export let selectedLocationList;
  export let docid = null;
  export let disabled = false;
  export let filters;

  const recordTypes = {
    item: "Item",
    type: "Type",
    collection: "Opening",
    document_note: "Note",
  };

  let modal;
  let scope = "all"; // "all", "selected", "document"
  let sortBy = null;
  let sortOrder = "asc";
  let locationData = [];
  let categoryList = [];
  let sectionList = [];
  let columns = [];
  let selectionSet = [];
  let addToSelectionButton;
  let removeFromSelectionButton;
  let countBy = "location_category";

  let showItems = true;
  let showOpenings = true;
  let showTypes = true;
  let showNotes = true;

  $: filteredLocationData = filterData(locationData, showItems, showOpenings, showTypes, showNotes);
  $: totalsRow = totalLocationData(filteredLocationData, categoryList, sectionList);
  $: doc = docid ? $group.documents[docid] : null;
  $: refresh(doc);

  function refresh(doc) {
    if (!doc?.data.count_by) return;
    countBy = doc.data.count_by;
  }

  function filterData(locationData, i, o, t, n) {
    if (!locationData || !locationData.length) return [];

    return locationData.filter((loc) => {
      if (!showItems && loc.type === "Item") return false;
      if (!showOpenings && loc.type === "Opening") return false;
      if (!showTypes && loc.type === "Type") return false;
      if (!showNotes && loc.type === "Note") return false;
      return true;
    });
  }

  function copyFromTable(e) {
    const { selection } = e.detail;
    const result = [];

    for (let r = selection[0]; r <= selection[2]; r++) {
      const row = [];
      const datum = r === filteredLocationData.length ? totalsRow : filteredLocationData[r];
      for (let c = selection[1]; c <= selection[3]; c++) {
        const col = columns[c];
        if (r === -1) {
          row.push(col.label);
        } else {
          let value = datum[col.prop];
          row.push(value);
        }
      }
      result.push(row);
    }

    const dest = result.map((row) => row.join("\t")).join("\n");
    copyToClipboard(dest);
  }

  function relatedRecord(location, grp) {
    if (!location) return null;
    if (location.record_type === "type") {
      return grp.types[location.record_id];
    } else if (location.record_type === "document_note") {
      return grp.document_notes[location.record_id];
    } else {
      return grp.items[location.record_id];
    }
  }

  function getLocationData(locations, categoryList, sectionList, grp) {
    let grouped = {};

    locations.forEach((loc) => {
      if (!loc.record_id) return;

      if (!grouped[loc.record_id]) {
        const record = relatedRecord(loc, grp);
        if (!record) return;

        grouped[loc.record_id] = {
          mark: record.mark,
          type: recordTypes[loc.record_type],
          quantity: record.quantity ?? "",
          record_id: loc.record_id,
        };

        categoryList.forEach((cat) => {
          grouped[loc.record_id][cat] = 0;
        });

        sectionList.forEach((section) => {
          grouped[loc.record_id][section] = 0;
        });

        const page = grp.documents[loc.document_id]?.data.pages[loc.page_id];
        const sec = page?.section ? `section_${page.section}` : "Unlabeled";
        const cat = loc.category ? `category_${loc.category}` : "Uncategorized";
        if (!loc.ignore_for_auto_count) {
          grouped[loc.record_id][sec] += 1;
          grouped[loc.record_id][cat] += 1;
          grouped[loc.record_id].all = 1;
        } else {
          grouped[loc.record_id].all = 0;
        }
      } else {
        const page = grp.documents[loc.document_id]?.data.pages[loc.page_id];
        const sec = page?.section ? `section_${page.section}` : "Unlabeled";
        const cat = loc.category ? `category_${loc.category}` : "Uncategorized";
        if (!loc.ignore_for_auto_count) {
          grouped[loc.record_id][sec] += 1;
          grouped[loc.record_id][cat] += 1;
          grouped[loc.record_id].all += 1;
        }
      }
    });

    return Object.values(grouped);
  }

  function totalLocationData(locationData, categoryList, sectionList) {
    const catTotals = categoryList.reduce(
      (acc, cat) => {
        acc[cat] = 0;
        return acc;
      },
      { mark: "", type: "", all: 0 },
    );

    const secTotals = sectionList.reduce((acc, sec) => {
      acc[sec] = 0;
      return acc;
    }, {});

    const totals = { ...catTotals, ...secTotals };

    locationData.forEach((loc) => {
      categoryList.forEach((cat) => {
        totals[cat] += loc[cat] || 0;
      });
      sectionList.forEach((sec) => {
        totals[sec] += loc[sec] || 0;
      });
      totals.all += loc.all;
    });

    return totals;
  }

  function sortLocationData(by) {
    if (sortBy === by && sortOrder === "asc") {
      sortOrder = "desc";
    } else {
      sortOrder = "asc";
    }
    sortBy = by;

    locationData.sort((a, b) => {
      if (["mark", "type"].includes(sortBy)) {
        return sortOrder === "asc" ? a[sortBy].localeCompare(b[sortBy]) : b[sortBy].localeCompare(a[sortBy]);
      }

      return sortOrder === "asc" ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
    });

    locationData = locationData;
  }

  function setData() {
    const allLocations = Object.values(docLocations).flat();

    let locations;
    if (scope === "selected") {
      locations = selectedLocationList || [];
    } else if (scope === "document" && docid) {
      locations = (docLocations[docid] || []).filter((loc) => isLocationVisible(loc, $group, filters));
    } else {
      locations = allLocations || [];
    }

    const categories = locations.reduce((acc, loc) => {
      const cat = loc.category ? `category_${loc.category}` : "Uncategorized";
      const label = loc.category || "Uncategorized";
      if (!acc[cat]) {
        acc[cat] = { label, prop: cat };
      }
      return acc;
    }, {});
    categoryList = Object.keys(categories).sort((a, b) => a.localeCompare(b));
    const catCols = categoryList.map((cat) => categories[cat]);
    const sections = docid
      ? doc.data.pages.order.reduce((acc, pageId, index) => {
          const page = doc.data.pages[pageId];
          if (page.section && page[page.section] == null) acc[page.section] = index;
          return acc;
        }, {})
      : {};
    const sectionCols = Object.keys(sections)
      .sort((a, b) => sections[a] - sections[b])
      .map((sec) => ({ label: sec, prop: `section_${sec}` }));
    sectionList = sectionCols.map((s) => s.prop);

    if (countBy === "sheet_section") {
      columns = [
        { label: "Mark", prop: "mark" },
        { label: "Type", prop: "type" },
        { label: "Quantity", prop: "quantity" },
        ...sectionCols,
        { label: "All", prop: "all" },
      ];
    } else {
      columns = [
        { label: "Mark", prop: "mark" },
        { label: "Type", prop: "type" },
        { label: "Quantity", prop: "quantity" },
        ...catCols,
        { label: "All", prop: "all" },
      ];
    }

    locationData = getLocationData(locations, categoryList, sectionList, $group);
  }

  async function toggleGrid() {
    sortBy = null;

    if (selectedLocationList.length) {
      scope = "selected";
    } else if (docid && docLocations[docid]?.length) {
      scope = "document";
    } else {
      scope = "all";
    }

    await tick();

    setData();
    modal.open();
  }

  function updateScope() {
    setData();
  }

  function toggleFilter(type) {
    if (type === "items") {
      showItems = !showItems;
    } else if (type === "openings") {
      showOpenings = !showOpenings;
    } else if (type === "types") {
      showTypes = !showTypes;
    } else if (type === "notes") {
      showNotes = !showNotes;
    }
  }

  function apply(category) {
    const updates = filteredLocationData
      .filter((loc) => {
        if (loc.type === "Item" || loc.type === "Opening") return true;
        return false;
      })
      .map((loc) => {
        const quantity = loc[category] ?? 0;
        return { type: "item", id: loc.record_id, prop: "quantity", value: quantity };
      });

    group.update(updates);

    const updatedLocs = updates.reduce((acc, update) => {
      acc[update.id] = update.value;
      return acc;
    }, {});

    locationData = locationData.map((loc) => {
      if (updatedLocs[loc.record_id] != undefined) {
        loc.quantity = updatedLocs[loc.record_id];
      }

      return loc;
    });
  }

  function getSelectedLocs(selection) {
    const sel = {};
    selection.forEach((s) => {
      for (let row = s[0]; row <= s[2]; row++) {
        for (let col = s[1]; col <= s[3]; col++) {
          sel[`${row}.${col}`] = true;
        }
      }
    });

    const docLocs = docLocations[docid] || [];
    const rowIndices = filteredLocationData.reduce((acc, row, index) => {
      acc[row.record_id] = index;
      return acc;
    }, {});

    const allCols = [0, 1, 2, categoryList.length + 3];
    const catIndices = categoryList.reduce((acc, cat, index) => {
      acc[cat] = index + 3;
      return acc;
    }, {});

    const filtered = docLocs.filter((l) => {
      const cat = l.category || "Uncategorized";
      const row = rowIndices[l.record_id];
      const col = catIndices[cat];

      if (sel[`${row}.${col}`]) return true;
      if (allCols.some((c) => sel[`${row}.${c}`])) return true;

      return false;
    });

    return filtered.map((l) => l.id);
  }

  function addToSelection() {
    const locs = getSelectedLocs(selectionSet);
    if (!locs.length) return;
    selectedLocations.select(...locs);
  }

  async function removeFromSelection() {
    const locs = getSelectedLocs(selectionSet);
    if (!locs.length) return;
    selectedLocations.deselect(...locs);
  }

  async function updateCountBy(e) {
    countBy = e.detail.value;
    const data = cloneDeep(doc.data);
    data.count_by = e.detail.value;
    await group.update([{ type: "document", id: docid, prop: "data", value: data }]);
    setData();
  }

  onMount(() => {
    eb.on("toggle-count-grid", toggleGrid);

    return () => {
      eb.unsubscribe("toggle-count-grid", toggleGrid);
    };
  });
</script>

<Modal bind:this={modal} width="80%" closeable>
  <div slot="title">Count Grid</div>
  <div slot="content" class="flex flex-col overflow-y-hidden gap-4">
    <div class="flex-none w-full flex items-center justify-between text-xs">
      <div class="flex items-center flex-wrap gap-2">
        <div class="w-72">
          <SelectInput
            label="Scope"
            bind:value={scope}
            border
            options={[
              { label: "All Markers", value: "all" },
              ...(selectedLocationList.length ? [{ label: "Selected Markers", value: "selected" }] : []),
              ...(docid ? [{ label: "Markers in Current Document", value: "document" }] : []),
            ]}
            on:input={updateScope} />
        </div>
        {#if docid}
          <div class="w-72">
            <SelectInput
              label="Count By"
              value={countBy}
              border
              options={[
                { label: "Mark Category", value: "location_category" },
                { label: "Sheet Type", value: "sheet_section" },
              ]}
              on:input={updateCountBy} />
          </div>
        {/if}
      </div>
      <div class="flex items-center gap-1 filter-buttons">
        <button class:selected={showItems} on:click={() => toggleFilter("items")}> Items </button>
        <button class:selected={showOpenings} on:click={() => toggleFilter("openings")}> Openings </button>
        <button class:selected={showTypes} on:click={() => toggleFilter("types")}> Types </button>
        <button class:selected={showNotes} on:click={() => toggleFilter("notes")}> Notes </button>
      </div>
    </div>
    <div class="grow overflow-y-auto">
      <CopyableTable
        classes="w-full table-auto text-xs"
        let:selected
        on:copy={copyFromTable}
        bind:selection={selectionSet}
        ignoreClick={[addToSelectionButton, removeFromSelectionButton]}>
        <thead>
          {#if !disabled}
            {@const list = countBy === "sheet_section" ? sectionList : categoryList}
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              {#each list as cat}
                <th class="p-1"
                  ><button class="btn-text" on:click={() => apply(cat)}>Apply to Qty</button></th>
              {/each}
              <th class="p-1"
                ><button class="btn-text" on:click={() => apply("all")}>Apply to Qty</button></th>
            </tr>
          {/if}
          <tr class="bg-gray-100 border italic border-gray-200 text-gray-500 header-row">
            <th class="w-10"></th>
            {#each columns as col, colIndex}
              <th data-column={colIndex} data-row="-1" class:bg-blue-100={selected[`-1.${colIndex}`]}>
                <button on:click={() => sortLocationData(col.prop)}>
                  <div>{col.label}</div>
                  <div class:invisible={sortBy !== col.prop} class:visible={sortBy === col.prop}>
                    {#if sortOrder === "asc"}
                      <CaretUp />
                    {:else}
                      <CaretDown />
                    {/if}
                  </div>
                </button>
              </th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#each filteredLocationData as loc, index}
            <tr class="data-row" class:last-row={index === filteredLocationData.length - 1}>
              <td class="border-r border-gray-200 bg-gray-100 text-gray-500 text-center">{index + 1}</td>
              {#each columns as col, colIndex}
                <td
                  data-row={index}
                  data-column={colIndex}
                  class:bg-blue-100={selected[`${index}.${colIndex}`]}>{loc[col.prop]}</td>
              {/each}
            </tr>
          {/each}
          {#if totalsRow}
            {@const list = countBy === "sheet_section" ? sectionList : categoryList}
            <tr class="font-bold border-l border-b border-r border-gray-200">
              <td class="bg-gray-100"></td>
              <td
                colspan="3"
                data-row={locationData.length}
                data-column={0}
                class:bg-blue-100={selected[`${locationData.length}.0`]}>Total</td>
              {#each list as cat, catIndex}
                <td
                  data-row={locationData.length}
                  data-column={catIndex + 2}
                  class:bg-blue-100={selected[`${locationData.length}.${catIndex + 2}`]}>{totalsRow[cat]}</td>
              {/each}
              <td
                data-row={locationData.length}
                data-column={list.length + 2}
                class:bg-blue-100={selected[`${locationData.length}.${list.length + 2}`]}>{totalsRow.all}</td>
            </tr>
          {/if}
        </tbody>
      </CopyableTable>
    </div>
    <div class="flex-none flex items-center justify-end gap-4 text-xs">
      {#if docid && selectionSet.length}
        <button
          bind:this={addToSelectionButton}
          class="btn-text"
          disabled={!selectionSet.length || scope === "selected"}
          on:click={addToSelection}>Add to Selection</button>
        <button
          bind:this={removeFromSelectionButton}
          class="btn-text"
          disabled={!selectionSet.length}
          on:click={removeFromSelection}>Remove from Selection</button>
      {:else}
        <div>&nbsp;</div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="scss">
  td {
    @apply px-2 border-r border-gray-200 font-normal;
  }

  th {
    @apply px-2 font-normal;
  }

  .header-row th {
    @apply border-r border-gray-200;

    button {
      @apply text-left italic w-full flex items-center justify-between;
    }
  }

  tr.data-row {
    border-left: 1px solid oklch(92.8% 0.006 264.531);
    border-bottom: 1px solid oklch(92.8% 0.006 264.531);

    &.last-row {
      border-bottom: 1px solid oklch(27.8% 0.033 256.848);
    }
  }

  .filter-buttons {
    button {
      @apply px-2 py-1 rounded-full text-xs bg-gray-200;

      &.selected {
        @apply bg-black text-white;
      }
    }
  }
</style>
